import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

/**
 * Determine a CSS flex value from a given string. Defaults to "1 1 auto"
 */
const getFlexValue = value =>
  !value ? '1 1 auto' : `${parseFloat(value)}` !== value ? `0 0 ${value}` : `${value} 1 0%`

/**
 * Determine a CSS align-self value from a given string
 */
const getAlignValue = value => (value === 'start' ? 'flex-start' : value === 'end' ? 'flex-end' : value)

/**
 * A flex line controlling how the children are supposed to display : In rows or in columns with custom gap, custom alignments and custom space distribution
 */
const Line = styled.div`
  display: flex;

  ${({ justify }) =>
    css`
      justify-content: ${getAlignValue(justify)};
    `};

  ${({ vertical, reversed }) => css`
    flex-direction: ${(vertical ? 'column' : 'row') + (reversed ? '-reverse' : '')};
  `};

  ${({ layout }) =>
    (layout || '').split(' ').map(
      (value, index) => css`
        & > :nth-child(${index + 1}n) {
          flex: ${getFlexValue(value)};
        }
      `
    )};

  ${({ align }) =>
    (align || '').split(' ').map(
      (value, index) => css`
        & > :nth-child(${index + 1}n) {
          align-self: ${getAlignValue(value)};
        }
      `
    )};

  ${({ scrollable, vertical }) =>
    scrollable
      ? css`
          overflow-y: ${vertical ? 'auto' : 'hidden'};
          overflow-x: ${vertical ? 'hidden' : 'auto'};
          & > * {
            flex-shrink: 0 !important;
          }
        `
      : vertical
      ? css`
          & > * {
            min-height: 0;
          }
        `
      : ''};

  ${({ gap, vertical }) =>
    css`
      & > :not(:last-child) {
        margin-${vertical ? 'bottom' : 'right'}: ${gap};
      }
    `};
`

Line.defaultProps = {
  scrollable: false,
  gap: '.5rem',
  justify: 'stretch',
  align: 'stretch',
  layout: '',
  vertical: false,
  reversed: false,
}

Line.propTypes = {
  /**
   * Whether or not the container is scrollable (children do not shrink)
   */
  scrollable: PropTypes.bool,
  /**
   * CSS value of the gap between children
   */
  gap: PropTypes.string,
  /**
   * String containing many CSS value align-self, each one applied to each child.
   * This value is n-repeating : first item will define a :nth-child(1n) property, second item will define a :nth-child(2n) property...
   */
  align: PropTypes.string,
  /**
   * CSS value justify-content
   */
  justify: PropTypes.string,
  /**
   * String containing many values (number without unit for growing factor, number with unit for the exact size, or "auto" for the exact computed size) separated by spaces defining how the child is supposed to spread
   * This value is n-repeating : first item will define a :nth-child(1n) property, second item will define a :nth-child(2n) property...
   */
  layout: PropTypes.string,
  /**
   * Whether the line is vertical or not
   */
  vertical: PropTypes.bool,
  /**
   * Whether the content order is reversed or not
   */
  reversed: PropTypes.bool,
  /**
   * Component to use instead of the default 'div'
   */
  as: PropTypes.elementType,
}

export default Line
